import { makeAutoObservable, runInAction } from "mobx";
import {
  IsString,
  IsNotEmpty,
  IsBoolean,
  validate,
  type ValidationError,
} from "class-validator";
import {
  adminFaqList,
  adminFaqCreate,
  adminFaqUpdate,
  adminFaqDelete,
  adminFeedbackTopicList,
  adminFeedbackTopicCreate,
  adminFeedbackTopicUpdate,
  adminFeedbackTopicDelete,
  adminFeedbackSettingsGet,
  adminFeedbackSettingsUpdate,
} from "@pro-sto/api-client";
import type {
  FaqResponseDto,
  TopicResponseDto,
  FeedbackSettingsResponseDto,
} from "@pro-sto/api-client";
import { toastStore } from "./ToastStore";
import { feedbackStore } from "./FeedbackStore";
import { validateNotificationSettings, sanitizeNotificationForm } from "@/pages/FeedbackSettingsPage/notification-validation";
import type { NotificationSettingsFormState } from "@/pages/FeedbackSettingsPage/notification-validation";

export type SettingsDeleteKind = "faq" | "topic";

export interface DeleteTarget {
  kind: SettingsDeleteKind;
  uuid: string;
  label: string;
}

export interface FaqFormState {
  question: string;
  answer: string;
  is_active: boolean;
}

export interface TopicFormState {
  name: string;
  is_active: boolean;
}

function mapSettingsToForm(
  settings: FeedbackSettingsResponseDto,
): NotificationSettingsFormState {
  return sanitizeNotificationForm({
    notification_emails: [...settings.notification_emails],
    push_phones: [...settings.push_phones],
    sms_escalation_enabled: settings.sms_escalation_enabled,
    sms_escalation_delay_minutes: settings.sms_escalation_delay_minutes,
    sms_escalation_phones: [...settings.sms_escalation_phones],
  });
}

const emptyNotificationsForm = (): NotificationSettingsFormState => ({
  notification_emails: [],
  push_phones: [],
  sms_escalation_enabled: false,
  sms_escalation_delay_minutes: 30,
  sms_escalation_phones: [],
});

function cloneNotificationsForm(
  form: NotificationSettingsFormState,
): NotificationSettingsFormState {
  return {
    notification_emails: [...form.notification_emails],
    push_phones: [...form.push_phones],
    sms_escalation_enabled: form.sms_escalation_enabled,
    sms_escalation_delay_minutes: form.sms_escalation_delay_minutes,
    sms_escalation_phones: [...form.sms_escalation_phones],
  };
}

class FaqFormDto {
  @IsString()
  @IsNotEmpty({ message: "Вопрос обязателен" })
  question = "";

  @IsString()
  @IsNotEmpty({ message: "Ответ обязателен" })
  answer = "";

  @IsBoolean()
  is_active = true;
}

class TopicFormDto {
  @IsString()
  @IsNotEmpty({ message: "Название обязательно" })
  name = "";

  @IsBoolean()
  is_active = true;
}

function mapValidationErrors(errors: ValidationError[]): Record<string, string> {
  const result: Record<string, string> = {};
  for (const error of errors) {
    if (error.constraints) {
      result[error.property] = Object.values(error.constraints)[0] ?? "";
    }
  }
  return result;
}

function reorderByIndex<T>(items: T[], fromIndex: number, toIndex: number): T[] {
  const next = [...items];
  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);
  return next;
}

const emptyFaqForm = (): FaqFormState => ({
  question: "",
  answer: "",
  is_active: true,
});

const emptyTopicForm = (): TopicFormState => ({
  name: "",
  is_active: true,
});

class FeedbackSettingsStore {
  faqs: FaqResponseDto[] = [];
  topics: TopicResponseDto[] = [];
  isLoading = false;

  faqOrderMode = false;
  faqOrderDraft: FaqResponseDto[] | null = null;
  faqOrderSnapshot: string[] | null = null;
  isFaqsOrderSaving = false;

  topicOrderMode = false;
  topicOrderDraft: TopicResponseDto[] | null = null;
  topicOrderSnapshot: string[] | null = null;
  isTopicsOrderSaving = false;

  faqFormOpen = false;
  faqEditingUuid: string | null = null;
  faqForm: FaqFormState = emptyFaqForm();
  faqFormErrors: Record<string, string> = {};
  faqSubmitAttempted = false;
  faqSubmitting = false;

  topicFormOpen = false;
  topicEditingUuid: string | null = null;
  topicForm: TopicFormState = emptyTopicForm();
  topicFormErrors: Record<string, string> = {};
  topicSubmitAttempted = false;
  topicSubmitting = false;

  deleteTarget: DeleteTarget | null = null;
  isDeleting = false;

  isNotificationsLoading = false;
  isNotificationsSaving = false;
  notificationsForm: NotificationSettingsFormState = emptyNotificationsForm();
  notificationsSnapshot: NotificationSettingsFormState | null = null;
  notificationsFormErrors: Record<string, string> = {};
  notificationsSubmitAttempted = false;
  notificationsUpdatedAt: string | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  get isFaqEditing(): boolean {
    return this.faqEditingUuid != null;
  }

  get isTopicEditing(): boolean {
    return this.topicEditingUuid != null;
  }

  get displayFaqs(): FaqResponseDto[] {
    return this.faqOrderMode && this.faqOrderDraft
      ? this.faqOrderDraft
      : this.faqs;
  }

  get displayTopics(): TopicResponseDto[] {
    return this.topicOrderMode && this.topicOrderDraft
      ? this.topicOrderDraft
      : this.topics;
  }

  get isFaqOrderDirty(): boolean {
    if (!this.faqOrderMode || !this.faqOrderDraft || !this.faqOrderSnapshot) {
      return false;
    }
    const current = this.faqOrderDraft.map((item) => item.uuid).join(",");
    const original = this.faqOrderSnapshot.join(",");
    return current !== original;
  }

  get isTopicOrderDirty(): boolean {
    if (!this.topicOrderMode || !this.topicOrderDraft || !this.topicOrderSnapshot) {
      return false;
    }
    const current = this.topicOrderDraft.map((item) => item.uuid).join(",");
    const original = this.topicOrderSnapshot.join(",");
    return current !== original;
  }

  get isNotificationsDirty(): boolean {
    if (!this.notificationsSnapshot) return false;
    return (
      JSON.stringify(this.notificationsForm) !==
      JSON.stringify(this.notificationsSnapshot)
    );
  }

  loadAll = async () => {
    this.isLoading = true;
    this.isNotificationsLoading = true;
    try {
      const [faqs, topics, notifications] = await Promise.all([
        adminFaqList(),
        adminFeedbackTopicList(),
        adminFeedbackSettingsGet(),
      ]);
      runInAction(() => {
        this.faqs = [...(faqs ?? [])].sort(
          (a, b) => a.sort_order - b.sort_order,
        );
        this.topics = [...(topics ?? [])].sort(
          (a, b) => a.sort_order - b.sort_order,
        );
        this.applyNotificationsSettings(notifications);
        this.isLoading = false;
        this.isNotificationsLoading = false;
      });
    } catch {
      runInAction(() => {
        this.isLoading = false;
        this.isNotificationsLoading = false;
      });
      toastStore.showError("Не удалось загрузить настройки");
    }
  };

  private applyNotificationsSettings = (
    settings: FeedbackSettingsResponseDto,
  ) => {
    const form = mapSettingsToForm(settings);
    this.notificationsForm = form;
    this.notificationsSnapshot = cloneNotificationsForm(form);
    this.notificationsUpdatedAt = settings.updated_at;
    this.notificationsFormErrors = {};
    this.notificationsSubmitAttempted = false;
  };

  resetNotificationsForm = () => {
    if (!this.notificationsSnapshot) return;
    this.notificationsForm = cloneNotificationsForm(this.notificationsSnapshot);
    this.notificationsFormErrors = {};
    this.notificationsSubmitAttempted = false;
  };

  setNotificationsField = <K extends keyof NotificationSettingsFormState>(
    field: K,
    value: NotificationSettingsFormState[K],
  ) => {
    this.notificationsForm[field] = value;
    if (field === "sms_escalation_enabled" && value === false) {
      delete this.notificationsFormErrors.sms_escalation_phones;
    }
  };

  addNotificationEmail = (email: string) => {
    this.notificationsForm.notification_emails = [
      ...this.notificationsForm.notification_emails,
      email,
    ];
  };

  removeNotificationEmail = (index: number) => {
    this.notificationsForm.notification_emails =
      this.notificationsForm.notification_emails.filter((_, i) => i !== index);
  };

  addPushPhone = (phone: string) => {
    this.notificationsForm.push_phones = [
      ...this.notificationsForm.push_phones,
      phone,
    ];
  };

  removePushPhone = (index: number) => {
    this.notificationsForm.push_phones = this.notificationsForm.push_phones.filter(
      (_, i) => i !== index,
    );
  };

  addSmsEscalationPhone = (phone: string) => {
    this.notificationsForm.sms_escalation_phones = [
      ...this.notificationsForm.sms_escalation_phones,
      phone,
    ];
  };

  removeSmsEscalationPhone = (index: number) => {
    const phones = this.notificationsForm.sms_escalation_phones.filter(
      (_, i) => i !== index,
    );
    this.notificationsForm.sms_escalation_phones = phones;

    if (phones.every((phone) => !phone.trim())) {
      this.notificationsForm.sms_escalation_enabled = false;
      delete this.notificationsFormErrors.sms_escalation_phones;
    }
  };

  submitNotifications = async () => {
    this.notificationsSubmitAttempted = true;
    const sanitizedForm = sanitizeNotificationForm(this.notificationsForm);
    const errors = await validateNotificationSettings(sanitizedForm);
    if (Object.keys(errors).length > 0) {
      runInAction(() => {
        this.notificationsFormErrors = errors;
      });
      return;
    }

    runInAction(() => {
      this.notificationsForm = cloneNotificationsForm(sanitizedForm);
      this.notificationsFormErrors = {};
    });

    this.isNotificationsSaving = true;

    try {
      const updated = await adminFeedbackSettingsUpdate({
        requestBody: { ...sanitizedForm },
      });
      runInAction(() => {
        this.applyNotificationsSettings(updated);
        this.isNotificationsSaving = false;
      });
      toastStore.showSuccess("Настройки уведомлений сохранены");
    } catch {
      runInAction(() => {
        this.isNotificationsSaving = false;
      });
      toastStore.showError("Не удалось сохранить настройки уведомлений");
    }
  };

  reset = () => {
    this.closeFaqForm();
    this.closeTopicForm();
    this.cancelFaqOrderMode();
    this.cancelTopicOrderMode();
    this.deleteTarget = null;
    this.resetNotificationsForm();
    this.notificationsSnapshot = null;
    this.notificationsUpdatedAt = null;
  };

  enterFaqOrderMode = () => {
    if (this.faqs.length < 2) return;
    this.faqOrderMode = true;
    this.faqOrderDraft = [...this.faqs];
    this.faqOrderSnapshot = this.faqs.map((item) => item.uuid);
  };

  cancelFaqOrderMode = () => {
    this.faqOrderMode = false;
    this.faqOrderDraft = null;
    this.faqOrderSnapshot = null;
  };

  moveFaqDraft = (fromIndex: number, toIndex: number) => {
    if (!this.faqOrderDraft || fromIndex === toIndex) return;
    this.faqOrderDraft = reorderByIndex(this.faqOrderDraft, fromIndex, toIndex);
  };

  saveFaqOrder = async () => {
    if (
      !this.faqOrderDraft ||
      !this.isFaqOrderDirty ||
      this.isFaqsOrderSaving
    ) {
      return;
    }

    const previous = [...this.faqs];
    const reordered = this.faqOrderDraft.map((item, index) => ({
      ...item,
      sort_order: index,
    }));
    const toUpdate = reordered.filter((item) => {
      const orig = previous.find((p) => p.uuid === item.uuid);
      return orig?.sort_order !== item.sort_order;
    });

    this.isFaqsOrderSaving = true;

    try {
      const updated = await Promise.all(
        toUpdate.map((item) =>
          adminFaqUpdate({
            uuid: item.uuid,
            requestBody: { sort_order: item.sort_order },
          }),
        ),
      );
      runInAction(() => {
        const map = new Map(updated.map((item) => [item.uuid, item]));
        this.faqs = reordered
          .map((item) => map.get(item.uuid) ?? item)
          .sort((a, b) => a.sort_order - b.sort_order);
        this.isFaqsOrderSaving = false;
      });
      this.cancelFaqOrderMode();
      toastStore.showSuccess("Порядок сохранён");
    } catch {
      runInAction(() => {
        this.isFaqsOrderSaving = false;
      });
      toastStore.showError("Не удалось сохранить порядок");
    }
  };

  enterTopicOrderMode = () => {
    if (this.topics.length < 2) return;
    this.topicOrderMode = true;
    this.topicOrderDraft = [...this.topics];
    this.topicOrderSnapshot = this.topics.map((item) => item.uuid);
  };

  cancelTopicOrderMode = () => {
    this.topicOrderMode = false;
    this.topicOrderDraft = null;
    this.topicOrderSnapshot = null;
  };

  moveTopicDraft = (fromIndex: number, toIndex: number) => {
    if (!this.topicOrderDraft || fromIndex === toIndex) return;
    this.topicOrderDraft = reorderByIndex(
      this.topicOrderDraft,
      fromIndex,
      toIndex,
    );
  };

  saveTopicOrder = async () => {
    if (
      !this.topicOrderDraft ||
      !this.isTopicOrderDirty ||
      this.isTopicsOrderSaving
    ) {
      return;
    }

    const previous = [...this.topics];
    const reordered = this.topicOrderDraft.map((item, index) => ({
      ...item,
      sort_order: index,
    }));
    const toUpdate = reordered.filter((item) => {
      const orig = previous.find((p) => p.uuid === item.uuid);
      return orig?.sort_order !== item.sort_order;
    });

    this.isTopicsOrderSaving = true;

    try {
      const updated = await Promise.all(
        toUpdate.map((item) =>
          adminFeedbackTopicUpdate({
            uuid: item.uuid,
            requestBody: { sort_order: item.sort_order },
          }),
        ),
      );
      runInAction(() => {
        const map = new Map(updated.map((item) => [item.uuid, item]));
        this.topics = reordered
          .map((item) => map.get(item.uuid) ?? item)
          .sort((a, b) => a.sort_order - b.sort_order);
        this.isTopicsOrderSaving = false;
      });
      feedbackStore.invalidateTopicsCache();
      this.cancelTopicOrderMode();
      toastStore.showSuccess("Порядок сохранён");
    } catch {
      runInAction(() => {
        this.isTopicsOrderSaving = false;
      });
      toastStore.showError("Не удалось сохранить порядок");
    }
  };

  openFaqCreate = () => {
    this.faqEditingUuid = null;
    this.faqForm = emptyFaqForm();
    this.faqFormErrors = {};
    this.faqSubmitAttempted = false;
    this.faqFormOpen = true;
  };

  openFaqEdit = (faq: FaqResponseDto) => {
    this.faqEditingUuid = faq.uuid;
    this.faqForm = {
      question: faq.question,
      answer: faq.answer,
      is_active: faq.is_active,
    };
    this.faqFormErrors = {};
    this.faqSubmitAttempted = false;
    this.faqFormOpen = true;
  };

  closeFaqForm = () => {
    this.faqFormOpen = false;
    this.faqEditingUuid = null;
    this.faqForm = emptyFaqForm();
    this.faqFormErrors = {};
    this.faqSubmitAttempted = false;
  };

  setFaqField = <K extends keyof FaqFormState>(
    field: K,
    value: FaqFormState[K],
  ) => {
    this.faqForm[field] = value;
  };

  submitFaq = async () => {
    this.faqSubmitAttempted = true;
    const dto = Object.assign(new FaqFormDto(), this.faqForm);
    const errors = await validate(dto);
    if (errors.length > 0) {
      runInAction(() => {
        this.faqFormErrors = mapValidationErrors(errors);
      });
      return;
    }

    this.faqSubmitting = true;

    try {
      if (this.faqEditingUuid) {
        const updated = await adminFaqUpdate({
          uuid: this.faqEditingUuid,
          requestBody: {
            question: dto.question,
            answer: dto.answer,
            is_active: dto.is_active,
          },
        });
        runInAction(() => {
          this.faqs = this.faqs
            .map((item) => (item.uuid === updated.uuid ? updated : item))
            .sort((a, b) => a.sort_order - b.sort_order);
        });
        toastStore.showSuccess("Вопрос обновлён");
      } else {
        const created = await adminFaqCreate({
          requestBody: {
            question: dto.question,
            answer: dto.answer,
            is_active: dto.is_active,
            sort_order: this.faqs.length,
          },
        });
        runInAction(() => {
          this.faqs = [...this.faqs, created].sort(
            (a, b) => a.sort_order - b.sort_order,
          );
        });
        toastStore.showSuccess("Вопрос добавлен");
      }
      runInAction(() => {
        this.faqSubmitting = false;
      });
      this.closeFaqForm();
    } catch {
      runInAction(() => {
        this.faqSubmitting = false;
      });
      toastStore.showError("Не удалось сохранить вопрос");
    }
  };

  openTopicCreate = () => {
    this.topicEditingUuid = null;
    this.topicForm = emptyTopicForm();
    this.topicFormErrors = {};
    this.topicSubmitAttempted = false;
    this.topicFormOpen = true;
  };

  openTopicEdit = (topic: TopicResponseDto) => {
    this.topicEditingUuid = topic.uuid;
    this.topicForm = {
      name: topic.name,
      is_active: topic.is_active,
    };
    this.topicFormErrors = {};
    this.topicSubmitAttempted = false;
    this.topicFormOpen = true;
  };

  closeTopicForm = () => {
    this.topicFormOpen = false;
    this.topicEditingUuid = null;
    this.topicForm = emptyTopicForm();
    this.topicFormErrors = {};
    this.topicSubmitAttempted = false;
  };

  setTopicField = <K extends keyof TopicFormState>(
    field: K,
    value: TopicFormState[K],
  ) => {
    this.topicForm[field] = value;
  };

  submitTopic = async () => {
    this.topicSubmitAttempted = true;
    const dto = Object.assign(new TopicFormDto(), this.topicForm);
    const errors = await validate(dto);
    if (errors.length > 0) {
      runInAction(() => {
        this.topicFormErrors = mapValidationErrors(errors);
      });
      return;
    }

    this.topicSubmitting = true;

    try {
      if (this.topicEditingUuid) {
        const updated = await adminFeedbackTopicUpdate({
          uuid: this.topicEditingUuid,
          requestBody: {
            name: dto.name,
            is_active: dto.is_active,
          },
        });
        runInAction(() => {
          this.topics = this.topics
            .map((item) => (item.uuid === updated.uuid ? updated : item))
            .sort((a, b) => a.sort_order - b.sort_order);
        });
        toastStore.showSuccess("Тема обновлена");
      } else {
        const created = await adminFeedbackTopicCreate({
          requestBody: {
            name: dto.name,
            is_active: dto.is_active,
            sort_order: this.topics.length,
          },
        });
        runInAction(() => {
          this.topics = [...this.topics, created].sort(
            (a, b) => a.sort_order - b.sort_order,
          );
        });
        toastStore.showSuccess("Тема добавлена");
      }
      feedbackStore.invalidateTopicsCache();
      runInAction(() => {
        this.topicSubmitting = false;
      });
      this.closeTopicForm();
    } catch {
      runInAction(() => {
        this.topicSubmitting = false;
      });
      toastStore.showError("Не удалось сохранить тему");
    }
  };

  requestDelete = (target: DeleteTarget) => {
    this.deleteTarget = target;
  };

  cancelDelete = () => {
    this.deleteTarget = null;
  };

  confirmDelete = async () => {
    if (!this.deleteTarget) return;
    this.isDeleting = true;
    const { kind, uuid } = this.deleteTarget;

    try {
      if (kind === "faq") {
        await adminFaqDelete({ uuid });
        runInAction(() => {
          this.faqs = this.faqs.filter((item) => item.uuid !== uuid);
        });
        toastStore.showSuccess("Вопрос удалён");
      } else {
        await adminFeedbackTopicDelete({ uuid });
        runInAction(() => {
          this.topics = this.topics.filter((item) => item.uuid !== uuid);
        });
        feedbackStore.invalidateTopicsCache();
        toastStore.showSuccess("Тема удалена");
      }
      runInAction(() => {
        this.isDeleting = false;
        this.deleteTarget = null;
      });
    } catch {
      runInAction(() => {
        this.isDeleting = false;
      });
      toastStore.showError("Не удалось удалить");
    }
  };
}

export const feedbackSettingsStore = new FeedbackSettingsStore();
export type { FeedbackSettingsStore };
export type { NotificationSettingsFormState } from "@/pages/FeedbackSettingsPage/notification-validation";
